// OLIVIUM · Integrity — chain verification view.
//
// Live mode calls olv_verify_event_chain(); demo mode simulates
// a healthy chain with an explicit tamper toggle. Missing RPC
// surfaces "Awaiting chain verification service" without crashing.
//
// This route is also the one place the shell exercises the full
// OLIVIUM trust loop end-to-end:
//   sign-in → bearer token
//   "Record a trust-loop seal" → POST /api/olivium/seal
//   server: insert olv_document_seals + OliviumAudit.logSeal
//   Realtime: olv_audit_trail INSERT fans out to useAuditFeed
//   chain.refresh() re-runs olv_verify_event_chain
// Every UI state below renders only what the server actually returned.

import { useState } from 'react'
import { shortHash, timeAgo } from '../../shell/styles'
import { useAuditFeed } from '../../hooks/useAuditFeed'
import { useChainIntegrity } from '../../hooks/useChainIntegrity'
import { getRuntime } from '../../hooks/_mode'
import { api, ApiError, isAuthError } from '../../api/client'

// Browser-native SHA-256. No dependency, works in every modern browser.
// Returns a lowercase hex string of the 32-byte digest.
async function sha256Hex(text: string): Promise<string> {
  const data = new TextEncoder().encode(text)
  const buf = await crypto.subtle.digest('SHA-256', data)
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

type SealState =
  | { status: 'idle' }
  | { status: 'pending'; docId: string }
  | {
      status: 'success'
      docId: string
      docHash: string
      audit: {
        id: string
        sequence: number
        action: string
        chain_hash: string
        previous_hash: string
        created_at: string
      }
    }
  | { status: 'error'; code: string; message: string }

export default function OliviumChain() {
  const { events, authRequired: feedAuthRequired } = useAuditFeed()
  const chain = useChainIntegrity(events.length)
  const { live } = getRuntime()
  const [seal, setSeal] = useState<SealState>({ status: 'idle' })

  // Protected action: exercise the real OLIVIUM trust loop. We never
  // fabricate a success state — every UI affordance below renders only
  // what /api/olivium/seal actually returned.
  async function recordTrustLoopSeal() {
    if (!live) {
      setSeal({
        status: 'error',
        code: 'demo_mode',
        message: 'Trust-loop seal requires live mode (VITE_OLIVIUM_MODE=live with Supabase creds).',
      })
      return
    }
    const docId = `trust-loop-${Date.now()}`
    setSeal({ status: 'pending', docId })

    try {
      const docHash = await sha256Hex(`OLIVIUM trust-loop proof · ${docId}`)
      const res = (await api.olivium.seal(docId, docHash)) as {
        ok?: boolean
        seal?: { document_id: string; document_hash: string; sealed_by: string }
        audit?: SealState extends { status: 'success'; audit: infer A } ? A : never
      }
      if (!res?.ok || !res.audit) {
        setSeal({
          status: 'error',
          code: 'seal_no_entry',
          message: 'Seal endpoint returned no audit entry. Nothing to display.',
        })
        return
      }
      setSeal({
        status: 'success',
        docId,
        docHash,
        audit: res.audit,
      })
      // Re-run chain verification so the "checked" count reflects the row we just wrote.
      await chain.refresh()
    } catch (err) {
      if (isAuthError(err)) {
        setSeal({
          status: 'error',
          code: 'auth_required',
          message: 'Sign in first — /api/olivium/seal requires a bearer token.',
        })
        return
      }
      if (err instanceof ApiError) {
        setSeal({ status: 'error', code: err.code, message: err.message })
        return
      }
      setSeal({
        status: 'error',
        code: 'unexpected',
        message: err instanceof Error ? err.message : String(err),
      })
    }
  }

  const banner = chain.missingRpc
    ? { color: 'rgba(255,184,77,0.32)', bg: 'rgba(255,184,77,0.08)', text: '#ffd08a' }
    : chain.valid
    ? { color: 'rgba(85,211,127,0.28)', bg: 'rgba(85,211,127,0.08)', text: '#a8efbf' }
    : { color: 'rgba(255,82,82,0.32)',  bg: 'rgba(255,82,82,0.08)',  text: '#ff9f9f' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <div
            style={{
              fontSize: 11,
              color: '#7f8db4',
              fontFamily: 'var(--font-mono)',
              letterSpacing: '0.16em',
              marginBottom: 6,
            }}
          >
            OLIVIUM · INTEGRITY
          </div>
          <h1
            style={{
              fontSize: 24,
              fontWeight: 700,
              margin: 0,
              color: '#edf2ff',
              fontFamily: 'var(--font-brand)',
            }}
          >
            Chain Verification
          </h1>
          <div style={{ fontSize: 13, color: '#97a7cf', marginTop: 4 }}>
            {live
              ? 'olv_verify_event_chain() walks the audit chain.'
              : 'Demo chain — toggle tamper to simulate an integrity issue.'}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          {!live && (
            <button
              type="button"
              onClick={() => chain.setDemoTamper(!chain.demoTamper)}
              style={btnSecondary}
            >
              {chain.demoTamper ? 'Disable tamper' : 'Simulate tamper'}
            </button>
          )}
          <button type="button" onClick={chain.refresh} style={btnPrimary}>
            Recheck chain
          </button>
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${banner.color}`,
          background: banner.bg,
          borderRadius: 18,
          padding: 18,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 6, color: banner.text }}>
          {chain.loading
            ? 'Checking chain…'
            : chain.missingRpc
            ? 'Awaiting chain verification service'
            : chain.valid
            ? 'Chain verified'
            : 'Chain requires attention'}
        </div>
        {chain.message && !chain.missingRpc && (
          <div style={{ fontSize: 13, color: '#d4def7' }}>{chain.message}</div>
        )}
        <div style={{ fontSize: 12, color: '#97a7cf', marginTop: 6 }}>
          Checked entries: {chain.checked}
        </div>
      </div>

      {/* Trust-loop seal — one real end-to-end protected action in the live shell.
          Sign-in establishes the bearer token; clicking here POSTs to
          /api/olivium/seal which writes olv_document_seals + an
          OliviumAudit.logSeal entry; Realtime fans the new row back to
          useAuditFeed; we then re-run chain verification. No state below is
          fabricated — everything shown comes from the actual server response. */}
      <section
        style={{
          border: '1px solid rgba(124,92,252,0.28)',
          background: 'rgba(124,92,252,0.06)',
          borderRadius: 18,
          padding: 18,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 12 }}>
          <div>
            <div
              style={{
                fontSize: 10,
                color: '#a7a0e3',
                fontFamily: 'var(--font-mono)',
                letterSpacing: '0.16em',
                marginBottom: 4,
              }}
            >
              OLIVIUM · TRUST LOOP
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#edeaff' }}>
              Record a trust-loop seal
            </div>
            <div style={{ fontSize: 12, color: '#a7a0e3', marginTop: 4, maxWidth: 560 }}>
              Writes a real entry to <code style={{ fontFamily: 'var(--font-mono)' }}>olv_document_seals</code>{' '}
              and appends a <code style={{ fontFamily: 'var(--font-mono)' }}>document_sealed</code> row to the
              audit chain. The Signal feed will pick it up via Realtime; the chain
              banner above will re-run verification.
            </div>
          </div>
          <button
            type="button"
            onClick={recordTrustLoopSeal}
            disabled={seal.status === 'pending'}
            style={{
              ...btnPrimary,
              opacity: seal.status === 'pending' ? 0.6 : 1,
              cursor: seal.status === 'pending' ? 'wait' : 'pointer',
              flexShrink: 0,
            }}
          >
            {seal.status === 'pending' ? 'Sealing…' : 'Record seal'}
          </button>
        </div>

        {!live && seal.status === 'idle' && (
          <div style={{ fontSize: 12, color: '#ffd08a', fontFamily: 'var(--font-mono)' }}>
            Demo mode — live seal is not available. Set VITE_OLIVIUM_MODE=live with Supabase creds to exercise the real loop.
          </div>
        )}

        {seal.status === 'pending' && (
          <div style={{ fontSize: 12, color: '#d4def7', fontFamily: 'var(--font-mono)' }}>
            POST /api/olivium/seal … document_id {seal.docId}
          </div>
        )}

        {seal.status === 'error' && (
          <div
            style={{
              border: '1px solid rgba(255,82,82,0.32)',
              background: 'rgba(255,82,82,0.08)',
              borderRadius: 10,
              padding: '10px 12px',
              color: '#ff9f9f',
              fontSize: 12,
              fontFamily: 'var(--font-mono)',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Seal failed · {seal.code}
            </div>
            <div style={{ color: '#ffd4d4' }}>{seal.message}</div>
          </div>
        )}

        {seal.status === 'success' && (
          <div
            style={{
              border: '1px solid rgba(85,211,127,0.28)',
              background: 'rgba(85,211,127,0.06)',
              borderRadius: 10,
              padding: '12px 14px',
              display: 'grid',
              gap: 6,
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 700, color: '#a8efbf' }}>
              Seal recorded · audit entry appended
            </div>
            <SealField label="document_id" value={seal.docId} />
            <SealField label="document_hash" value={seal.docHash} mono />
            <SealField label="audit.id" value={seal.audit.id} mono />
            <SealField label="audit.sequence" value={`#${seal.audit.sequence}`} />
            <SealField label="audit.action" value={seal.audit.action} />
            <SealField label="audit.chain_hash" value={seal.audit.chain_hash} mono />
            <SealField label="audit.previous_hash" value={seal.audit.previous_hash} mono />
            <SealField label="audit.created_at" value={seal.audit.created_at} mono />
          </div>
        )}
      </section>

      <section
        style={{
          border: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.035)',
          borderRadius: 18,
          overflow: 'hidden',
        }}
      >
        {chain.authRequired || feedAuthRequired ? (
          <div style={{ padding: 24 }}>
            <div style={{ fontSize: 13, color: '#97a7cf' }}>
              Sign in to inspect the live chain and related audit entries.
            </div>
          </div>
        ) : (
          events.slice(0, 25).map((e) => (
          <div
            key={e.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 16,
              padding: '14px 16px',
              borderBottom: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{e.action || e.event_type}</div>
              <div style={{ fontSize: 11, color: '#93a5cc' }}>
                Seq {e.sequence ?? '—'} · {timeAgo(e.created_at)}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: '#91a4cf', fontFamily: 'var(--font-mono)' }}>
                prev {shortHash(e.previous_hash)}
              </div>
              <div style={{ fontSize: 11, color: '#d7e3ff', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
                this {shortHash(e.chain_hash || e.event_hash)}
              </div>
            </div>
          </div>
          ))
        )}
      </section>
    </div>
  )
}

const btnPrimary = {
  border: '1px solid rgba(95,142,255,0.42)',
  background: 'rgba(66,110,255,0.16)',
  color: '#edf2ff',
  borderRadius: 10,
  padding: '8px 14px',
  cursor: 'pointer',
  fontWeight: 700,
  fontSize: 12,
  fontFamily: 'inherit',
} as const

const btnSecondary = {
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'rgba(255,255,255,0.04)',
  color: '#edf2ff',
  borderRadius: 10,
  padding: '8px 14px',
  cursor: 'pointer',
  fontWeight: 600,
  fontSize: 12,
  fontFamily: 'inherit',
} as const

function SealField({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 12, fontSize: 11, lineHeight: 1.5 }}>
      <span
        style={{
          minWidth: 130,
          color: '#7f8db4',
          fontFamily: 'var(--font-mono)',
          letterSpacing: '0.02em',
        }}
      >
        {label}
      </span>
      <span
        style={{
          color: '#d7e3ff',
          fontFamily: mono ? 'var(--font-mono)' : 'inherit',
          wordBreak: 'break-all',
        }}
      >
        {value}
      </span>
    </div>
  )
}
